import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { useUpdateCompanyParametersBO } from "@/api/company.api";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  ParametersImporterFormData,
  ParametersImporterSchema,
} from "@/types/forms/bo/clientsParameters.form";
import { useGetAllImporters } from "@/api/importer.api";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";

type UpdateImporterModalProps = {
  closeModal: () => void;
  importerId: string | null;
  title: string;
};

export const UpdateImporterModal = ({
  closeModal,
  importerId,
  title,
}: UpdateImporterModalProps) => {
  const { t } = useTranslation("boClients");
  const [companyId] = useLocation().pathname.split("/").slice(-1);
  const { data: importers } = useGetAllImporters();

  const { mutateAsync: updateCompanyParametersBO, isPending } = useUpdateCompanyParametersBO();

  const importerForm = useForm<ParametersImporterFormData>({
    resolver: zodResolver(ParametersImporterSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      importerId: importerId ?? "",
    },
  });

  const onSubmit: SubmitHandler<ParametersImporterFormData> = async (data) => {
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: data,
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  return (
    <Modal
      open
      onClose={closeModal}
      title={title}
      size="sm"
      contentClassName="max-w-[480px]"
      footer={
        <Button type="submit" form="importerForm" disabled={isPending} className="w-full">
          {isPending ? <StyledSpinner /> : t("importerModal.submit")}
        </Button>
      }
    >
      <form
        id="importerForm"
        onSubmit={importerForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 pt-2"
      >
        <ControlledSelectV2
          options={
            importers?.data?.map((i) => ({
              label: i.name,
              value: i.id,
            })) || []
          }
          control={importerForm.control}
          name="importerId"
          contentClassName="w-full"
        />
      </form>
    </Modal>
  );
};
